import { useSetModal } from "@/shared/atoms/useModal";
import usePosOrderDeliveryMethod from "@/shared/atoms/usePosOrderDeliveryMethodAtom";
import usePosTakeout from "@/shared/atoms/usePosTakeout";
import useIsOrderEditingHook from "@/shared/hooks/useIsOrderEditingHook";
import useIsWaiter from "@/shared/hooks/useIsWaiter";
import usePosOrderResetHook from "@/shared/hooks/usePosOrderResetHook";
import {
  useHasSession,
  useSessionId,
  useShowPayments,
} from "@/shared/hooks/usePosOrderTableSessionHook";
import usePosOrderTipHook from "@/shared/hooks/usePosOrderTipHook";
import useOrderSessionDeleteMutation from "@/shared/mutations/orderSessions/useOrderSessionDeleteMutation";
import { DeliveryMethods } from "@/shared/utils/enum";
import { useTranslation } from "react-i18next";
import Icon from "../Icon/Icon";
import Spinner from "../Spinner/Spinner";
import Tooltip from "@/Components/Tooltip/Tooltip";
import { useSetPosOrderCurrentAction } from "@/shared/atoms/usePosOrderCurrentAction";
import { useCustomProductAction } from "../PosMenuAction/CustomProductAction";
import useActionClick from "../PosMenuAction/useActionClick";
import { useEffect, useRef } from "react";
import { cn } from "@/shared/utils/cn";

const PosCartSideItem = ({
  isPending = false,
  bg = "primary",
  set,
  text,
  iconVariant,
  disabled = false,
}) => {
  return (
    <Tooltip content={text}>
      <div>
        <div
          onClick={disabled ? undefined : set}
          className={cn(
            `flex items-center justify-center rounded-md w-24 h-16`,
            disabled
              ? "bg-disabled cursor-not-allowed"
              : `bg-${bg} cursor-pointer`
          )}
        >
          {isPending ? (
            <Spinner color="white" size="0.25rem" />
          ) : (
            <Icon className="size-9 fill-white" variants={iconVariant} />
          )}
        </div>
      </div>
    </Tooltip>
  );
};

const PosCartSide = () => {
  const [takeout, setTakeout] = usePosTakeout();
  const isWaiter = useIsWaiter();
  const hasSession = useHasSession();
  const sessionId = useSessionId();
  const showPayments = useShowPayments();
  const { isOrderEditing } = useIsOrderEditingHook();
  const reset = usePosOrderResetHook();
  const { t } = useTranslation();
  const setModalGlobal = useSetModal();
  const { setTip } = usePosOrderTipHook();
  const setModal = useSetModal();
  const destroy = useOrderSessionDeleteMutation();
  const [deliveryMethod] = usePosOrderDeliveryMethod();
  const setCurrentAction = useSetPosOrderCurrentAction();
  const customProductAction = useCustomProductAction();
  const handleCustomProductAction = useActionClick(customProductAction);
  const tipClickRef = useRef(false);

  useEffect(() => {
    if (tipClickRef.current) {
      handleCustomProductAction();
      tipClickRef.current = false;
    }
  }, [tipClickRef.current]);

  const handleReset = () => {
    if (hasSession) {
      return destroy.mutateAsync([sessionId]).then(() => {
        reset();
      });
    }
    reset();
  };

  return (
    <div className="flex w-full flex-wrap gap-2">
      <PosCartSideItem
        set={() => setModal("posOrderResetCart")}
        text={t("posOrders.emptyTheCart")}
        iconVariant="trash"
      />

      {!isWaiter && (
        <PosCartSideItem
          set={() => setModalGlobal("posOrderDiscount")}
          text={t("posOrders.addDiscount")}
          iconVariant="percent"
        />
      )}
      <PosCartSideItem
        set={() => setModal("posOrderProduct")}
        text={t("posOrders.addCustomProduct")}
        iconVariant="edit"
        disabled={hasSession ? showPayments : false}
      />

      <PosCartSideItem
        set={() => setModal("posOrderDiscountCode")}
        text={t("posOrders.discountCode")}
        iconVariant="keyboard"
        disabled={hasSession ? showPayments : false}
      />
      {deliveryMethod === DeliveryMethods.POS_ORDER && (
        <PosCartSideItem
          set={() => setTakeout(!takeout)}
          text={t(takeout ? "takeout" : "dineIn")}
          iconVariant={takeout ? "basket" : "utensils"}
        />
      )}

      {(isOrderEditing || hasSession) && (
        <PosCartSideItem
          isPending={destroy.isPending}
          bg="var(--error)"
          set={() => handleReset()}
          text={t("reset")}
          iconVariant="times"
        />
      )}

      <PosCartSideItem
        set={() => {
          setCurrentAction({
            name: "tip",
            action: (tip) => {
              setTip(tip);
              tipClickRef.current = true;
            },
          });
        }}
        text={
          hasSession && !showPayments
            ? t("posOrders.tipInfo")
            : t("posOrders.tip")
        }
        iconVariant="sackDollar"
        disabled={hasSession ? !showPayments : false}
      />

      <PosCartSideItem
        set={() => setModal("posOrderRemark")}
        text={t("posOrders.addNote")}
        iconVariant="pencil"
      />
    </div>
  );
};

export default PosCartSide;
